import { customFetch } from '@agos/api-client'
import { tt, vapidKeyBytes } from '@agos/shared'

/**
 * Push to an external courier's phone - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 G, owner O139.
 *
 * The owner asked *"Len neviem že keď mame apku cez web že či chodia aj upozornenia keď neni priamo v apke"* - and
 * the answer is yes on Android with the browser closed, and on an iPhone once the app is on the home screen. This is
 * the client half: the phone registers the courier worker, subscribes with the server's VAPID key, and hands the
 * subscription to `POST /employees/my-push-subscription`.
 */

/** The worker the manager registers for this, and keeps when it evicts others (§9.282). */
export const COURIER_WORKER = '/courier-sw.js'

export const PUSH_STATE_PATH = '/api/v1/employees/my-push-subscription'

/**
 * What this phone can do about push.
 *
 * - `home-screen`: an iPhone in Safari, which delivers web push only to an app added to the home screen.
 * - `unsupported`: a browser with no service workers or no push.
 * - `supported`: it can subscribe.
 */
export type PushSupport = 'supported' | 'unsupported' | 'home-screen'

interface Environment {
  userAgent: string
  standalone: boolean
  hasServiceWorker: boolean
  hasPushManager: boolean
  hasNotification: boolean
}

export function pushSupportOf(environment: Environment): PushSupport {
  const apple = /iPhone|iPad|iPod/.test(environment.userAgent)
  if (apple && !environment.standalone) return 'home-screen'
  if (!environment.hasServiceWorker || !environment.hasPushManager || !environment.hasNotification) {
    return 'unsupported'
  }
  return 'supported'
}

export function currentEnvironment(): Environment {
  const nav = typeof navigator === 'undefined' ? undefined : navigator
  const win = typeof window === 'undefined' ? undefined : window
  return {
    userAgent: nav?.userAgent ?? '',
    standalone:
      (win?.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
      (nav as unknown as { standalone?: boolean } | undefined)?.standalone === true,
    hasServiceWorker: nav !== undefined && 'serviceWorker' in nav,
    hasPushManager: win !== undefined && 'PushManager' in win,
    hasNotification: win !== undefined && 'Notification' in win,
  }
}

/** The sentence under the switch for each state the phone can be in. */
export function pushNote(support: PushSupport, permission: string, subscribed: boolean): string {
  if (support === 'home-screen') {
    return tt('Na iPhone pridajte aplikáciu na plochu (Zdieľať, Pridať na plochu) a otvorte ju odtiaľ - inak upozornenia nechodia.')
  }
  if (support === 'unsupported') return tt('Tento prehliadač upozornenia nepodporuje. Skúste Chrome.')
  if (permission === 'denied') {
    return tt('Upozornenia sú v prehliadači zakázané - povoľte ich v nastaveniach stránky a skúste znova.')
  }
  return subscribed
    ? tt('Na tento telefón príde upozornenie, keď objednávka čaká na šoféra - aj keď je aplikácia zavretá.')
    : tt('Zapnite, aby vám prišlo upozornenie, keď objednávka čaká na šoféra.')
}

/** Why subscribing failed, said to the courier. */
export class PushRefused extends Error {}

function publicKeyOf(payload: unknown): string {
  const row = typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {}
  return typeof row.publicKey === 'string' ? row.publicKey : ''
}

/** Subscribe this phone, asking the courier for permission first. */
export async function subscribeCourier(): Promise<void> {
  const key = publicKeyOf(await customFetch<unknown>('/api/v1/push/public-key'))
  if (key === '') throw new PushRefused(tt('Upozornenia nie sú na serveri nastavené. Povedzte to prevádzke.'))
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new PushRefused(tt('Bez povolenia upozornení vám objednávky chodiť nebudú.'))
  }
  const registration = await navigator.serviceWorker.register(COURIER_WORKER, { scope: '/' })
  await navigator.serviceWorker.ready
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyBytes(key).buffer as ArrayBuffer,
    }))
  await customFetch<unknown>(PUSH_STATE_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subscriptionPayload(subscription.toJSON())),
  })
}

/** Stop the push to this phone, on the server and in the browser. */
export async function unsubscribeCourier(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration(COURIER_WORKER)
  const subscription = await registration?.pushManager.getSubscription()
  if (subscription === undefined || subscription === null) return
  await customFetch<unknown>(PUSH_STATE_PATH, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  })
  await subscription.unsubscribe()
}

/** Whether this phone already has a subscription. */
export async function isSubscribed(): Promise<boolean> {
  if (pushSupportOf(currentEnvironment()) !== 'supported') return false
  const registration = await navigator.serviceWorker.getRegistration(COURIER_WORKER)
  return (await registration?.pushManager.getSubscription()) != null
}

/** What the server takes: the endpoint and the two keys, nothing else the browser puts in its JSON. */
export function subscriptionPayload(json: PushSubscriptionJSON): {
  endpoint: string
  keys: { p256dh: string; auth: string }
} {
  return {
    endpoint: json.endpoint ?? '',
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  }
}
